/**
 * ProductCatalogPage — Admin page for viewing product definitions (config type #16).
 *
 * Products are Layer 2 config sets (DRR-095/096) that bundle:
 * - Required RuleSets (legislation)
 * - Activated workflows
 * - Settings + overrides
 * - Onboarding lenses + constellations
 *
 * Data-driven: all product data from backend `productDefinitions` query.
 * Route: /architect/products
 */

import { useMemo, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Badge,
  Paper,
  SimpleGrid,
  Select,
  Skeleton,
  Alert,
  Divider,
  List,
  ThemeIcon,
} from '@mantine/core';
import {
  IconPackage,
  IconAlertCircle,
  IconChecklist,
  IconSettings,
  IconScale,
  IconStars,
  IconEye,
} from '@tabler/icons-react';
import {
  useProductDefinitions,
  type ProductDefinition,
} from '@/features/tenant-setup/hooks/useProductDefinitions';

function statusColor(status: string): string {
  switch (status) {
    case 'active': return 'green';
    case 'draft': return 'yellow';
    case 'deprecated': return 'red';
    default: return 'gray';
  }
}

function provisioningColor(status: string): string {
  switch (status) {
    case 'provisioned': return 'green';
    case 'pending': return 'yellow';
    case 'failed': return 'red';
    default: return 'gray';
  }
}

export function ProductCatalogPage() {
  const [industryFilter, setIndustryFilter] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const { products, loading, error } = useProductDefinitions({
    industry: industryFilter,
  });

  // Extract unique industries for filter
  const industries = useMemo(() => {
    const set = new Set(products.map((p) => p.industry));
    return Array.from(set).map((i) => ({ value: i, label: i }));
  }, [products]);

  const selectedProduct = useMemo(
    () => products.find((p) => p.productId === selectedProductId),
    [products, selectedProductId],
  );

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon variant="light" size="lg">
              <IconPackage size={20} />
            </ThemeIcon>
            <div>
              <Title order={3}>Product Catalog</Title>
              <Text size="sm" c="dimmed">
                Layer 2 config sets — bundles of workflows, rules, and settings (DRR-095/096)
              </Text>
            </div>
          </Group>
          <Badge variant="light" size="lg">
            {products.length} products
          </Badge>
        </Group>

        {/* Filter */}
        <Group>
          <Select
            label="Industry"
            placeholder="All industries"
            data={industries}
            value={industryFilter}
            onChange={setIndustryFilter}
            clearable
            searchable
            w={250}
          />
        </Group>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            Failed to load products: {error.message}
          </Alert>
        )}

        {/* Product Grid */}
        {loading ? (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={200} radius="md" />
            ))}
          </SimpleGrid>
        ) : products.length === 0 ? (
          <Paper p="xl" withBorder ta="center">
            <Stack gap="md" align="center">
              <IconPackage size={48} color="gray" />
              <Text>No product definitions found. Configure products to populate.</Text>
            </Stack>
          </Paper>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
            {products.map((product) => (
              <Paper
                key={product.productId}
                p="md"
                withBorder
                shadow={selectedProductId === product.productId ? 'md' : 'xs'}
                style={{
                  cursor: 'pointer',
                  borderColor: selectedProductId === product.productId ? 'var(--mantine-color-blue-5)' : undefined,
                }}
                onClick={() => setSelectedProductId(product.productId)}
              >
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text fw={600} size="sm" truncate>
                      {product.name}
                    </Text>
                    <Badge size="xs" color={statusColor(product.status)}>
                      {product.status}
                    </Badge>
                  </Group>

                  {product.description && (
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {product.description}
                    </Text>
                  )}

                  <Group gap="xs">
                    <Badge size="xs" variant="outline">{product.industry}</Badge>
                    <Badge size="xs" variant="outline">{product.jurisdiction}</Badge>
                    <Badge size="xs" variant="light" color="blue">v{product.version}</Badge>
                  </Group>

                  <Divider />

                  <Group gap="xs" wrap="wrap">
                    <Badge size="xs" leftSection={<IconScale size={10} />}>
                      {product.requiredRulesets.length} rulesets
                    </Badge>
                    <Badge size="xs" leftSection={<IconChecklist size={10} />}>
                      {product.activatesWorkflows.length} workflows
                    </Badge>
                    <Badge size="xs" leftSection={<IconSettings size={10} />}>
                      {product.activatesSettings.length} settings
                    </Badge>
                    <Badge size="xs" leftSection={<IconStars size={10} />}>
                      {product.activatesConstellations.length} constellations
                    </Badge>
                    <Badge size="xs" leftSection={<IconEye size={10} />}>
                      {product.onboardingLenses.length} lenses
                    </Badge>
                  </Group>

                  <Badge
                    size="xs"
                    color={provisioningColor(product.provisioningStatus)}
                    variant="light"
                    fullWidth
                  >
                    Provisioning: {product.provisioningStatus}
                  </Badge>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        )}

        {/* Detail Panel */}
        {selectedProduct && (
          <Paper p="lg" withBorder shadow="sm">
            <Stack gap="md">
              <Group justify="space-between">
                <Title order={4}>{selectedProduct.name}</Title>
                <Badge>{selectedProduct.productId}</Badge>
              </Group>

              {selectedProduct.description && (
                <Text size="sm">{selectedProduct.description}</Text>
              )}

              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <div>
                  <Text size="sm" fw={600} mb="xs">Required RuleSets (Legislation)</Text>
                  <List size="xs" spacing={2}>
                    {selectedProduct.requiredRulesets.map((rs) => (
                      <List.Item key={rs}>{rs}</List.Item>
                    ))}
                  </List>
                </div>

                <div>
                  <Text size="sm" fw={600} mb="xs">Activated Workflows</Text>
                  <List size="xs" spacing={2}>
                    {selectedProduct.activatesWorkflows.map((wf) => (
                      <List.Item key={wf}>{wf}</List.Item>
                    ))}
                  </List>
                </div>

                <div>
                  <Text size="sm" fw={600} mb="xs">Subgraphs</Text>
                  <Group gap="xs">
                    {selectedProduct.subgraphs.map((sg) => (
                      <Badge key={sg} size="xs" variant="light">{sg}</Badge>
                    ))}
                  </Group>
                </div>

                <div>
                  <Text size="sm" fw={600} mb="xs">Onboarding Lenses</Text>
                  <List size="xs" spacing={2}>
                    {selectedProduct.onboardingLenses.map((l) => (
                      <List.Item key={l}>{l}</List.Item>
                    ))}
                  </List>
                </div>
              </SimpleGrid>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}
